/**
 * AddWorkoutModal - Modal for adding/editing a workout
 * Mobile-first responsive design with professional UX
 *
 * Features:
 * - Exercise type selection first
 * - Dynamic sets for strength training (each set has its own reps/weight)
 * - Comparison with previous workout
 * - Duration/distance for cardio
 * - Touch-friendly inputs
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Dumbbell, Check, Star, Loader2 } from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import { fitness } from '../../services/supabaseService';
import { coachRelationships } from '../../services/coachService';
import { format } from 'date-fns';
import { getExercisesByCategory } from '../../data/exercises';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import SetEditor from '../ui/SetEditor';

// Exercise types with Turkish labels (only 3 categories)
const EXERCISE_TYPES_TR = {
  fitness: { label: 'Fitness', emoji: '🏋️', color: 'blue' },
  kickbox: { label: 'Kickbox', emoji: '🥊', color: 'red' },
  cardio: { label: 'Kardiyo', emoji: '🏃', color: 'green' },
};

// Map old exercise_type values to new ones (strength -> fitness)
const normalizeExerciseType = (type) => {
  if (!type) return 'fitness';
  const mapping = {
    'strength': 'fitness',
    'flexibility': 'fitness',
    'sport': 'fitness',
    'other': 'fitness',
  };
  return mapping[type] || type;
};

const AddWorkoutModal = ({ isOpen, onClose }) => {
  const {
    selectedWorkout,
    selectedDate,
    selectedDayClientId,
    addWorkout,
    updateWorkout,
    deleteWorkout,
    exerciseNames,
    formatDate,
    favoriteExercises,
    toggleFavoriteExercise,
    completingScheduledWorkout,
  } = useFitnessStore();

  const { viewingClientId } = useCoachStore();

  const isEditing = !!selectedWorkout;
  const isCompletingSchedule = !!completingScheduledWorkout;
  const formRef = useRef(null);

  // Form state
  const [exerciseType, setExerciseType] = useState('fitness');
  const [exerciseName, setExerciseName] = useState('');
  const [sets, setSets] = useState([{ reps: '', weight: '' }]);
  const [weightUnit, setWeightUnit] = useState('kg');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [distanceKm, setDistanceKm] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(selectedDate || format(new Date(), 'yyyy-MM-dd'));

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Comparison state
  const [comparisonData, setComparisonData] = useState(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);

  // Companion mode state (coach working out alongside student)
  const [companionSets, setCompanionSets] = useState([{ reps: '', weight: '' }]);
  const [isCompanionView, setIsCompanionView] = useState(false);

  // Populate form when editing or completing a scheduled workout
  useEffect(() => {
    if (selectedWorkout) {
      setExerciseType(normalizeExerciseType(selectedWorkout.exercise_type));
      setExerciseName(selectedWorkout.exercise_name || '');
      setWeightUnit(selectedWorkout.weight_unit || 'kg');
      setDurationMinutes(selectedWorkout.duration_minutes?.toString() || '');
      setDistanceKm(selectedWorkout.distance_km?.toString() || '');
      setNotes(selectedWorkout.notes || '');
      setDate(selectedWorkout.date || selectedDate);

      if (selectedWorkout.reps_detail && Array.isArray(selectedWorkout.reps_detail)) {
        setSets(selectedWorkout.reps_detail.map((detail) => ({
          reps: detail.reps?.toString() || '',
          weight: detail.weight?.toString() || selectedWorkout.weight?.toString() || '',
        })));
      } else if (selectedWorkout.sets) {
        const newSets = [];
        for (let i = 0; i < selectedWorkout.sets; i++) {
          newSets.push({
            reps: selectedWorkout.reps?.toString() || '',
            weight: selectedWorkout.weight?.toString() || '',
          });
        }
        setSets(newSets.length > 0 ? newSets : [{ reps: '', weight: '' }]);
      } else {
        setSets([{
          reps: selectedWorkout.reps?.toString() || '',
          weight: selectedWorkout.weight?.toString() || ''
        }]);
      }
    } else if (completingScheduledWorkout) {
      setDate(completingScheduledWorkout.scheduled_date || selectedDate);
      setExerciseType(normalizeExerciseType(completingScheduledWorkout.exercise_type));
      setExerciseName('');
      setSets([{ reps: '', weight: '' }]);
      setDurationMinutes(completingScheduledWorkout.duration_minutes?.toString() || '');
      setDistanceKm('');
      setNotes(completingScheduledWorkout.notes || '');
    } else if (selectedDate) {
      setDate(selectedDate);
      setExerciseType('fitness');
      setExerciseName('');
      setSets([{ reps: '', weight: '' }]);
      setDurationMinutes('');
      setDistanceKm('');
      setNotes('');
      // Reset companion mode
      setCompanionSets([{ reps: '', weight: '' }]);
      setIsCompanionView(false);
    }
  }, [selectedWorkout, selectedDate, completingScheduledWorkout]);

  // Fetch comparison data when exercise name changes
  useEffect(() => {
    if (!exerciseName.trim() || exerciseType !== 'fitness' || !date) {
      setComparisonData(null);
      return;
    }

    const fetchComparison = async () => {
      setIsLoadingComparison(true);
      try {
        const targetClientId = viewingClientId || selectedDayClientId;

        if (targetClientId) {
          // Coach mode
          const clientWorkouts = await coachRelationships.getClientWorkouts(
            targetClientId,
            null,
            date
          );

          const previousWorkouts = clientWorkouts
            .filter(w => w.exercise_name === exerciseName && w.date < date)
            .sort((a, b) => {
              if (a.date !== b.date) return b.date.localeCompare(a.date);
              return (a.row_order || 0) - (b.row_order || 0);
            });

          if (previousWorkouts.length > 0) {
            const lastDate = previousWorkouts[0].date;
            const lastDateSets = previousWorkouts
              .filter(w => w.date === lastDate)
              .sort((a, b) => (a.row_order || 0) - (b.row_order || 0));

            setComparisonData({
              date: lastDate,
              sets: lastDateSets.map(s => ({
                weight: parseFloat(s.weight) || 0,
                reps: s.reps || 0,
              })),
            });
          } else {
            setComparisonData(null);
          }
        } else {
          // Normal user mode
          const previousSets = await fitness.getPreviousWorkoutSets(exerciseName, date, null);

          if (previousSets.length > 0) {
            setComparisonData({
              date: previousSets[0].date,
              sets: previousSets.map(s => ({
                weight: parseFloat(s.weight) || 0,
                reps: s.reps || 0,
              })),
            });
          } else {
            setComparisonData(null);
          }
        }
      } catch (error) {
        console.error('Error fetching comparison:', error);
        setComparisonData(null);
      } finally {
        setIsLoadingComparison(false);
      }
    };

    // Debounce the fetch
    const timer = setTimeout(fetchComparison, 300);
    return () => clearTimeout(timer);
  }, [exerciseName, exerciseType, date, viewingClientId, selectedDayClientId]);

  // Prevent body scroll when modal is open
  useBodyScrollLock(isOpen);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!exerciseName.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (exerciseType === 'fitness') {
        // Create separate record for EACH set
        if (isEditing) {
          // Update single set record
          const data = {
            date,
            exercise_name: exerciseName.trim(),
            exercise_type: exerciseType,
            muscle_group: null,
            sets: 1,
            reps: sets[0]?.reps ? parseInt(sets[0].reps) : null,
            weight: sets[0]?.weight ? parseFloat(sets[0].weight) : null,
            weight_unit: weightUnit,
            notes: notes.trim() || null,
          };
          await updateWorkout(selectedWorkout.id, data);
        } else {
          // Create a record for EACH set
          for (let i = 0; i < sets.length; i++) {
            const set = sets[i];
            if (!set.reps && !set.weight) continue; // Skip empty sets

            const setData = {
              date,
              exercise_name: exerciseName.trim(),
              exercise_type: exerciseType,
              muscle_group: null,
              sets: 1,
              reps: set.reps ? parseInt(set.reps) : null,
              weight: set.weight ? parseFloat(set.weight) : null,
              weight_unit: weightUnit,
              notes: i === 0 && notes.trim() ? notes.trim() : null,
              row_order: i, // Preserve set order
            };
            await addWorkout(setData);
          }
        }

        // Coach companion sets - also create separate records
        const hasCompanionSets = companionSets.some(s => s.reps || s.weight);
        if (viewingClientId && hasCompanionSets) {
          for (let i = 0; i < companionSets.length; i++) {
            const set = companionSets[i];
            if (!set.reps && !set.weight) continue;

            const coachSetData = {
              date,
              exercise_name: exerciseName.trim(),
              exercise_type: exerciseType,
              muscle_group: null,
              sets: 1,
              reps: set.reps ? parseInt(set.reps) : null,
              weight: set.weight ? parseFloat(set.weight) : null,
              weight_unit: weightUnit,
              notes: null,
              row_order: i,
            };
            await fitness.createWorkout(coachSetData);
          }
        }
      } else {
        // Cardio - single record as before
        const data = {
          date,
          exercise_name: exerciseName.trim(),
          exercise_type: exerciseType,
          muscle_group: null,
          duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
          distance_km: distanceKm ? parseFloat(distanceKm) : null,
          notes: notes.trim() || null,
        };

        if (isEditing) {
          await updateWorkout(selectedWorkout.id, data);
        } else {
          await addWorkout(data);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedWorkout && confirm('Bu antrenmanı silmek istediğinize emin misiniz?')) {
      await deleteWorkout(selectedWorkout.id);
    }
  };

  const handleExerciseTypeChange = (newType) => {
    setExerciseType(newType);
    setExerciseName('');
    setSets([{ reps: '', weight: '' }]);
    setComparisonData(null);
    // Reset companion mode
    setCompanionSets([{ reps: '', weight: '' }]);
    setIsCompanionView(false);
  };

  const getFilteredSuggestions = () => {
    let suggestions = [];

    if (exerciseType === 'fitness') {
      suggestions = getExercisesByCategory('fitness');
      if (!exerciseName) {
        const userExercises = exerciseNames.filter(e => !suggestions.includes(e));
        suggestions = [...suggestions, ...userExercises];
      }
    } else {
      suggestions = getExercisesByCategory(exerciseType);
    }

    if (exerciseName) {
      suggestions = suggestions.filter(
        name => name.toLowerCase().includes(exerciseName.toLowerCase())
      );
    }

    suggestions.sort((a, b) => {
      const aIsFav = favoriteExercises.includes(a);
      const bIsFav = favoriteExercises.includes(b);
      if (aIsFav && !bIsFav) return -1;
      if (!aIsFav && bIsFav) return 1;
      return a.localeCompare(b, 'tr');
    });

    return suggestions.slice(0, 15);
  };

  const filteredSuggestions = getFilteredSuggestions();

  if (!isOpen) return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full sm:max-w-lg bg-gray-900 border-t sm:border border-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl sm:m-4 overflow-y-auto"
        style={{ maxHeight: 'calc(100dvh - 40px)' }}
      >
        {/* Mobile drag indicator */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 sticky top-0 bg-gray-900 z-20">
          <div className="w-10 h-1 bg-gray-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 sticky top-4 sm:top-0 bg-gray-900 z-20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isEditing ? 'Düzenle' : isCompletingSchedule ? 'Detayları Gir' : 'Egzersiz Ekle'}
              </h2>
              <p className="text-sm text-gray-400">
                {formatDate(date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && (
              <button
                onClick={handleDelete}
                className="p-2.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="p-5 space-y-6">

          {/* Exercise Type */}
          <div>
            <label className="block text-base font-semibold text-white mb-3">
              Egzersiz Türü
            </label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(EXERCISE_TYPES_TR).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleExerciseTypeChange(key)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    exerciseType === key
                      ? 'border-orange-500 bg-orange-500/20 text-white'
                      : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <span className="text-2xl">{config.emoji}</span>
                  <span className="text-sm font-medium">{config.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Exercise Name */}
          <div className="relative">
            <label className="block text-base font-semibold text-white mb-3">
              Egzersiz Adı
            </label>
            <input
              type="text"
              value={exerciseName}
              onChange={(e) => setExerciseName(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Örn: Bench Press, Squat..."
              className="w-full px-4 py-4 bg-gray-800 border border-gray-700 rounded-xl text-white text-lg placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
              required
            />
            {isLoadingComparison && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2 mt-4">
                <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
              </div>
            )}
            <AnimatePresence>
              {showSuggestions && filteredSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto"
                >
                  {filteredSuggestions.map((name) => {
                    const isFavorite = favoriteExercises.includes(name);
                    return (
                      <div
                        key={name}
                        className="flex items-center border-b border-gray-700/50 last:border-0"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setExerciseName(name);
                            setShowSuggestions(false);
                          }}
                          className="flex-1 px-4 py-3 text-left text-gray-300 hover:bg-gray-700 transition-colors"
                        >
                          {name}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavoriteExercise(name);
                          }}
                          className="px-3 py-3 hover:bg-gray-700 transition-colors"
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isFavorite
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-500 hover:text-yellow-400'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Set Editor - Only show after exercise is selected */}
          <AnimatePresence>
            {exerciseType === 'fitness' && exerciseName.trim() && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <SetEditor
                  sets={isCompanionView ? companionSets : sets}
                  onSetsChange={isCompanionView ? setCompanionSets : setSets}
                  weightUnit={weightUnit}
                  onWeightUnitChange={setWeightUnit}
                  maxSets={20}
                  showQuickTemplates={true}
                  comparisonData={isCompanionView ? null : comparisonData}
                  // Companion mode props - only show when viewing client and not editing
                  companionMode={!!viewingClientId}
                  isCompanionView={isCompanionView}
                  onToggleCompanion={() => setIsCompanionView(!isCompanionView)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Cardio Fields */}
          <AnimatePresence>
            {exerciseType === 'cardio' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-2 gap-4"
              >
                <div>
                  <label className="block text-base font-semibold text-white mb-3">
                    Süre
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                      placeholder="30"
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-center text-lg font-medium focus:outline-none focus:border-orange-500"
                    />
                    <span className="text-gray-400">dk</span>
                  </div>
                </div>
                <div>
                  <label className="block text-base font-semibold text-white mb-3">
                    Mesafe
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.5"
                      value={distanceKm}
                      onChange={(e) => setDistanceKm(e.target.value)}
                      placeholder="5.0"
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-orange-400 text-center text-lg font-medium focus:outline-none focus:border-orange-500"
                    />
                    <span className="text-gray-400">km</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notes */}
          <div>
            <label className="block text-base font-semibold text-white mb-3">
              Notlar
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onFocus={(e) => {
                // iOS keyboard fix: scroll textarea into view
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              placeholder="Antrenman hakkında notlar..."
              rows={2}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center gap-3 p-5 border-t border-gray-800 bg-gray-900 pb-safe-area">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-4 text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={!exerciseName.trim() || isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors shadow-lg shadow-orange-600/20"
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {isEditing ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                <span>{isEditing ? 'Güncelle' : 'Ekle'}</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default AddWorkoutModal;
